import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { Badge } from 'src/registry/ui/Badge';
import { cn } from 'src/utils/shadcn';
import React from 'react';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { Link } from 'react-router-dom';
import { faArrowUpRight } from '@fortawesome/pro-regular-svg-icons';

export enum MEMBERSHIP_TEMPLATES_REQUIREMENTS {
    PRICING_PLAN = 'pricing_plan',
    ZONE_RULES = 'zone_rules',
    ALLOWED_TIMES = 'allocated_times',
    RESTRICTED_GATEWAYS = 'restricted_gateways',
}

type MembershipTemplateRequirementProps = {
    isRequirementComplete: boolean;
    isRequired: boolean;
    title: string;
    subTitle: string;
    requirementType: MEMBERSHIP_TEMPLATES_REQUIREMENTS;
    requirementSelection?: string;
    link?: string;
    isCustom?: boolean;
    children?: React.ReactNode;
};

const MembershipTemplateRequirement = ({
    isRequired,
    isRequirementComplete,
    title,
    subTitle,
    requirementSelection,
    children,
    requirementType,
    link,
    isCustom,
}: MembershipTemplateRequirementProps) => {
    const { formatMessage } = useIntl();

    const isRequiredAndComplete = isRequired && isRequirementComplete;
    const isRequiredAndNotComplete = isRequired && !isRequirementComplete;
    const requiredTitle = isRequired ? membershipTypeMessages.required : membershipTypeMessages.optional;

    const sharedColorMap = {
        text: isRequiredAndComplete
            ? 'text-green-700'
            : isRequiredAndNotComplete
              ? 'text-red-700'
              : isCustom
                ? 'text-blue-700'
                : 'text-gray-700',
        border: isRequiredAndComplete
            ? 'border-green-200'
            : isRequiredAndNotComplete
              ? 'border-red-200'
              : isCustom
                ? 'border-blue-200'
                : 'border-gray-200',
        badgeTheme: isRequiredAndComplete
            ? ('green' as const)
            : isRequiredAndNotComplete
              ? ('red' as const)
              : isCustom
                ? ('blue' as const)
                : ('gray' as const),
        icon: isRequiredAndComplete
            ? ICON_MAP.CHECK
            : isRequiredAndNotComplete
              ? ICON_MAP.CIRCLE_EXCLAMATION_SOLID
              : isCustom
                ? ICON_MAP.ARROW_UP_RIGHT_SQUARE
                : ICON_MAP.MINUS,
    };

    const colorMapping = React.useMemo(() => {
        switch (requirementType as MEMBERSHIP_TEMPLATES_REQUIREMENTS) {
            case MEMBERSHIP_TEMPLATES_REQUIREMENTS.PRICING_PLAN:
                return {
                    text: isRequiredAndComplete
                        ? 'text-green-700'
                        : isRequiredAndNotComplete
                          ? 'text-red-700'
                          : 'text-gray-700',
                    border: isRequiredAndComplete
                        ? 'border-green-200'
                        : isRequiredAndNotComplete
                          ? 'border-red-200'
                          : 'border-gray-200',
                    badgeTheme: isRequiredAndComplete
                        ? ('green' as const)
                        : isRequiredAndNotComplete
                          ? ('red' as const)
                          : ('gray' as const),
                    icon: isRequiredAndComplete
                        ? ICON_MAP.CHECK
                        : isRequiredAndNotComplete
                          ? ICON_MAP.CIRCLE_EXCLAMATION_SOLID
                          : ICON_MAP.MINUS,
                };
            case MEMBERSHIP_TEMPLATES_REQUIREMENTS.ZONE_RULES:
                return sharedColorMap;
            case MEMBERSHIP_TEMPLATES_REQUIREMENTS.ALLOWED_TIMES:
                return sharedColorMap;
            case MEMBERSHIP_TEMPLATES_REQUIREMENTS.RESTRICTED_GATEWAYS:
                return {
                    text: 'text-gray-700',
                    border: 'border-gray-200',
                    badgeTheme: 'gray' as const,
                    icon: ICON_MAP.MINUS,
                };
        }
    }, [requirementType, isRequiredAndComplete, isRequiredAndNotComplete, isCustom]);
    return (
        <div className="flex flex-row items-start justify-between gap-2">
            <div className="flex flex-3 gap-4">
                <Badge
                    theme={colorMapping.badgeTheme}
                    className={cn('h-8 w-8 rounded-lg border p-0', colorMapping.border)}
                >
                    <FontAwesomeIcon icon={colorMapping.icon} color={colorMapping.text} size="lg" />
                </Badge>

                <div className="flex flex-col">
                    <div className="flex flex-row items-end gap-2">
                        <div className="font-bold">{title}</div>
                        <div className="text-sm text-gray-500 uppercase">{formatMessage(requiredTitle)}</div>
                    </div>
                    <div>{subTitle}</div>
                </div>
            </div>
            {requirementSelection &&
                isRequirementComplete &&
                (link ? (
                    <Link
                        to={link}
                        className="cursor-pointer wrap-normal break-keep whitespace-normal text-green-700 hover:text-green-600"
                    >
                        {requirementSelection}
                        <FontAwesomeIcon icon={faArrowUpRight} />
                    </Link>
                ) : (
                    <div className={cn('flex flex-1 justify-end', colorMapping.text)}>{requirementSelection}</div>
                ))}

            {!isRequirementComplete && children && <div>{children}</div>}
        </div>
    );
};

export default MembershipTemplateRequirement;
